<script setup lang="ts">
import { ref, PropType } from "vue";
import { Department, Project, Trainer, TranslatedProduct } from "../../../types/PersologOnlineTypes";
import ProjectConfig from "./ProjectConfig.vue";

const props = defineProps({
    product: {
        type: Object as PropType<TranslatedProduct>,
        required: true,
    },
    trainer: {
        type: Object as PropType<Trainer>,
        required: true,
    },
    products: {
        type: Array as PropType<TranslatedProduct[]>,
        default: () => [],
    },
    departments: {
        type: Array as PropType<Department[]>,
        default: () => [],
    },
    project: {
        type: Object as PropType<Project>,
        required: true,
    },
    focus: {
        type: String,
        default: "",
    },
    productEditable: {
        type: Boolean,
    },
    hasDirectLink: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["valid", "project"]);

const constructedProject = ref<Project | undefined>(props.project);
const valid = ref<boolean>(true);

function handleProjectUpdate(updatedProject: Project) {
    constructedProject.value = updatedProject;
    emit("project", updatedProject);
}

function handleValidUpdate(isValid: boolean) {
    valid.value = isValid;
    emit("valid", isValid);
}

const validate = () => {
    const projectValue = constructedProject.value;
    if (projectValue === undefined) {
        emit("valid", false);
        return false;
    }

    if (valid.value) {
        const project: Project = {
            product: projectValue.product,
            defaultProduct: projectValue.product,
            language: projectValue.language,
            recipients: projectValue.recipients,
            focus: projectValue.focus,
            description: projectValue.description,
            anonymous: projectValue.anonymous,
            // This object replaces the one ProjectConfig built and is what the wizard creates the
            // project from, so a field left out here is lost: without it every new anonymous project
            // was stored as SELF_CHOSEN, whatever the trainer picked.
            pseudonymMode: projectValue.pseudonymMode,
            name: projectValue.name,
            trainer: props.trainer?.id,
            // Preview value only: the backend stamps the real creation date server-side and
            // ignores this, but the wizard's preview step renders it before the project exists.
            creation: new Date().toISOString(),
            participantDeletionNotification: projectValue.participantDeletionNotification,
            participantRetentionPeriod: projectValue.participantRetentionPeriod,
            participantDeletionDate: projectValue.participantDeletionDate,
            recipientReleaseDate: projectValue.recipientReleaseDate ?? null,
        };

        if (projectValue.additionalRecipient) {
            project.additionalRecipient = {
                email: projectValue.additionalRecipient.email,
                firstName: projectValue.additionalRecipient.firstName,
                lastName: projectValue.additionalRecipient.lastName,
            };
        } else project.additionalRecipient = null;

        emit("valid", true);
        emit("project", project);
        return true;
    }

    emit("valid", false);
    return false;
};
defineExpose({ validate });
</script>

<template>
    <ProjectConfig
        :products="products"
        :project="project"
        :product="product"
        :focus="focus"
        :trainer="trainer"
        :departments="departments"
        :product-editable="productEditable"
        :has-direct-link="hasDirectLink"
        @update:project="handleProjectUpdate"
        @update:valid="handleValidUpdate"
    />
</template>
